import React, { useEffect, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { capitalize, debounce } from "lodash"
import { Switch, FormControlLabel } from "@mui/material"

import { CHART_TYPE, ORDINAL_STRINGS } from "@/app/report/common/constants"
import { GraphSettings, GraphSettingsYAxis, SettingSectionProps } from "@/app/report/common/type"
import TextField from "@/components/form/textField/TextField"
import Selector from "@/components/form/selector/Selector"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import { coerceVerticalChartType, VERTICAL_CHART_TYPES } from "@/app/report/common/sections/graph"
import IconButton from "@/components/button/IconButton"
import SettingSectionItem from "@/app/report/components/setting/SettingSectionItem"
import MetricSetting from "@/app/report/components/setting/MetricSetting"
import ColorPicker from "@/app/report/components/setting/ColorPicker"
import DeleteItemDialog from "@/app/report/components/setting/DeleteItemDialog"
import SortSelector from "@/app/report/components/setting/SortSelector"
import Divider from "@/components/divider/Divider"
import { colors } from "@/components/colors"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook.ts"

const DEFAULT_Y_AXIS: GraphSettingsYAxis = {
  metric: "",
  chartType: "",
  color: colors.blue[900],
  manualScaleActive: false,
  manualScaleValue: null,
}

export default function PlayerGraphSection({
  settings,
  tempTemplate,
  setTempTemplate,
  sectionIndex,
}: SettingSectionProps<GraphSettings[]> & { sectionIndex: number }) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const [tempPlayerItems, setTempPlayerItems] = useState<GraphSettings[]>([])
  const [deleteItemIndex, setDeleteItemIndex] = useState<number | null>(null)
  const [manualScaleValues, setManualScaleValues] = useState<{
    [chartIndex: number]: { [yAxisIndex: number]: string }
  }>({})

  const getSortMenuOptions = (setting: GraphSettings) => {
    const valueChildren = setting.yAxis.map((yAxis) => ({
      key: yAxis.metric,
      label: translateKey(yAxis.metric),
      color: yAxis.color,
      children: [
        {
          key: `${yAxis.metric}-asc`,
          label: t("lowToHigh", { defaultValue: "Low to High" }),
          value: { key: yAxis.metric, order: "asc" as const },
        },
        {
          key: `${yAxis.metric}-desc`,
          label: t("highToLow", { defaultValue: "High to Low" }),
          value: { key: yAxis.metric, order: "desc" as const },
        },
      ],
    }))

    return [
      {
        key: "value",
        label: t("value"),
        children: valueChildren,
        is3D: true,
      },
      {
        key: "playerName",
        label: t("playerName"),
        children: [
          {
            key: "playerName-asc",
            label: t("aToZ", { defaultValue: "A - Z" }),
            value: { key: "playerName", order: "asc" as const },
          },
          {
            key: "playerName-desc",
            label: t("zToA", { defaultValue: "Z - A" }),
            value: { key: "playerName", order: "desc" as const },
          },
        ],
        is3D: false,
      },
      {
        key: "jersey",
        label: t("jersey"),
        children: [
          {
            key: "jersey-asc",
            label: t("lowToHigh", { defaultValue: "Low to High" }),
            value: { key: "jersey", order: "asc" as const },
          },
          {
            key: "jersey-desc",
            label: t("highToLow", { defaultValue: "High to Low" }),
            value: { key: "jersey", order: "desc" as const },
          },
        ],
        is3D: false,
      },
      {
        key: "position",
        label: t("position"),
        children: [
          {
            key: "position-asc",
            label: t("fwToGk", { defaultValue: "FW - GK" }),
            value: { key: "position", order: "asc" as const },
          },
          {
            key: "position-desc",
            label: t("gkToFw", { defaultValue: "GK - FW" }),
            value: { key: "position", order: "desc" as const },
          },
        ],
        is3D: false,
      },
    ]
  }

  const debouncedUpdateManualScale = useCallback(
    debounce((chartIndex: number, yAxisIndex: number, value: number | null) => {
      setTempPlayerItems((prev) => {
        const updated = [...prev]
        updated[chartIndex].yAxis[yAxisIndex] = {
          ...updated[chartIndex].yAxis[yAxisIndex],
          manualScaleValue: value,
        }

        return updated
      })
    }, 200),
    [],
  )

  useEffect(() => {
    setTempPlayerItems(settings)
  }, [settings])

  useEffect(() => {
    setTempTemplate((prev) => {
      if (!prev) {
        return null
      }
      const updatedContent = [...prev.content]
      updatedContent[sectionIndex] = { ...updatedContent[sectionIndex], settings: tempPlayerItems }

      return { ...prev, content: updatedContent }
    })
  }, [tempPlayerItems, sectionIndex])

  return (
    <>
      <div className="DailySettingPlayerGraphSectionCont">
        {tempPlayerItems.map((setting, index) => {
          return (
            <SettingSectionItem
              key={`scatter-setting-item-row-${index}`}
              onDeleteItem={index !== 0 ? () => setDeleteItemIndex(index) : undefined}>
              <div key="display" className="daily-setting-player-yAxis-row display-row">
                <p className="daily-setting-player-yAxis-label">{t("display")}</p>
                <div className="daily-setting-display-title-wrapper">
                  <TextField
                    label={t("title")}
                    value={setting.title}
                    onChange={(e) => {
                      const newValue = e.target.value

                      setTempPlayerItems((prev) => {
                        const updated = [...prev]
                        updated[index] = { ...updated[index], title: newValue }

                        return updated
                      })
                    }}
                    error={setting.title.length > 35 || setting.title.length < 1}
                    showMessage={setting.title.length > 35 || setting.title.length < 1}
                    message={
                      setting.title.length > 35
                        ? t("rules.max_len", { name: t("title"), max_len: 35 })
                        : t("rules.required")
                    }
                  />
                </div>
                {/* 값 표시·소수점 — #185(661fa0c6)에서 함께 지워졌다가 복구. 템플릿의 dataLabel·decimalPlace 를
                    그대로 쓰며 리포트 화면·내보내기·PDF 빌더가 이 값을 읽는다 */}
                <div className="daily-setting-display-options-wrapper">
                  <Selector
                    label={t("dataLabels")}
                    options={[
                      { value: "hideValues", label: t("hideValues") },
                      { value: "abovePlots", label: t("abovePlots") },
                      { value: "separateTable", label: t("separateTable") },
                    ]}
                    value={{
                      value: setting.dataLabel || "hideValues",
                      label: t(setting.dataLabel || "hideValues"),
                    }}
                    onChange={(_: unknown, newVal: { value: string; label: string } | null) => {
                      if (!newVal) {
                        return
                      }

                      setTempPlayerItems((prev) => {
                        const updated = [...prev]
                        updated[index] = { ...updated[index], dataLabel: newVal.value }

                        return updated
                      })
                    }}
                    placeholder={t("select")}
                  />
                  <Selector
                    label={t("decimalPlaces")}
                    disabled={(setting.dataLabel || "hideValues") === "hideValues"}
                    options={[
                      { value: "0", label: t("none") },
                      { value: "1", label: "0.1" },
                      { value: "2", label: "0.01" },
                    ]}
                    value={(() => {
                      const decimalValue = setting.decimalPlace?.toString() || "1"
                      const options = [
                        { value: "0", label: t("none") },
                        { value: "1", label: "0.1" },
                        { value: "2", label: "0.01" },
                      ]
                      const foundOption = options.find((opt) => opt.value === decimalValue)

                      return foundOption || { value: "1", label: "0.1" }
                    })()}
                    onChange={(_: unknown, newVal: { value: string; label: string } | null) => {
                      if (!newVal) {
                        return
                      }

                      setTempPlayerItems((prev) => {
                        const updated = [...prev]
                        updated[index] = {
                          ...updated[index],
                          decimalPlace: parseInt(newVal.value),
                        }

                        return updated
                      })
                    }}
                    placeholder={t("select")}
                  />
                </div>
              </div>
              <Divider />
              {/* 차트 방향 — 가로(선수가 X축, 기본) / 세로(선수 세로 나열). 없으면 가로. 세로면 X축 표시 스위치 (#185) */}
              <div
                key="orientation"
                className="daily-setting-player-yAxis-row daily-setting-orientation-row">
                <p className="daily-setting-player-yAxis-label">
                  {t("orientation", "Orientation")}
                </p>

                <RadioGroup
                  direction="row"
                  items={[
                    { label: t("orientationHorizontal", "Horizontal"), value: "horizontal" },
                    { label: t("orientationVertical", "Vertical"), value: "vertical" },
                  ]}
                  value={setting.orientation ?? "horizontal"}
                  onChange={(_, newVal) => {
                    if (!newVal) {
                      return
                    }
                    setTempPlayerItems((prev) => {
                      const updated = [...prev]
                      const orientation = newVal as "horizontal" | "vertical"
                      updated[index] = {
                        ...updated[index],
                        orientation,
                        // 세로는 막대·누적 막대만 — 다른 종류는 막대로 바꾼다 (2026-09-30 결정)
                        yAxis:
                          orientation === "vertical"
                            ? updated[index].yAxis.map((axis) => ({
                                ...axis,
                                chartType: coerceVerticalChartType(axis.chartType),
                              }))
                            : updated[index].yAxis,
                      }

                      return updated
                    })
                  }}
                />
                {/* Show X axis 스위치는 동작에 문제가 있어 설정 화면에서 숨겼다 (2026-10-15).
                    템플릿의 showAxis 값과 그리는 쪽은 그대로 두었으므로, 고치면 이 블록만 되살리면 된다 */}
              </div>
              <Divider />
              <div className="DailySettingPlayerYAxisCont">
                <div key="x-axis" className="daily-setting-player-yAxis-row">
                  <p className="daily-setting-player-yAxis-label">{t("xAxis")}</p>
                  <TextField
                    label={t("name")}
                    value={t("player")}
                    className="daily-setting-player-xAxis"
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                </div>
                {setting.yAxis.map((item, itemIndex) => {
                  return (
                    <div key={itemIndex} className="daily-setting-player-yAxis-row">
                      <p className="daily-setting-player-yAxis-label">{`${t(ORDINAL_STRINGS[itemIndex])} ${t("yAxis")}`}</p>
                      <MetricSetting
                        selectedMetric={item.metric}
                        setSelectedMetric={(value) => {
                          setTempPlayerItems((prev) => {
                            const updated = [...prev]
                            updated[index].yAxis[itemIndex] = {
                              ...updated[index].yAxis[itemIndex],
                              metric: value,
                            }

                            return updated
                          })
                        }}
                      />
                      <div className="daily-setting-player-chart-type-wrapper">
                        <Selector
                          label={t("chartType")}
                          options={Object.entries(CHART_TYPE)
                            .map(([, chartType]) => chartType)
                            // 세로 차트는 막대·누적 막대만 고를 수 있다 (2026-09-30 결정)
                            .filter(
                              (chartType) =>
                                (setting.orientation ?? "horizontal") !== "vertical" ||
                                VERTICAL_CHART_TYPES.includes(chartType),
                            )
                            .map((chartType) => ({
                              value: chartType,
                              label:
                                chartType === "stacked" ? t("stackedBar") : capitalize(chartType),
                            }))}
                          value={{
                            value: item.chartType,
                            label:
                              item.chartType === "stacked"
                                ? t("stackedBar")
                                : capitalize(item.chartType),
                          }}
                          renderOption={(props, option) => {
                            const { key, ...optionProps } = props

                            if (!option) {
                              return null
                            }

                            return (
                              <li key={key} {...optionProps}>
                                <p>{option.label}</p>
                              </li>
                            )
                          }}
                          onChange={(_: unknown, { value }: { value: string; label: string }) => {
                            setTempPlayerItems((prev) => {
                              const updated = [...prev]
                              updated[index].yAxis[itemIndex] = {
                                ...updated[index].yAxis[itemIndex],
                                chartType: value,
                              }

                              return updated
                            })
                          }}
                          placeholder={t("select")}
                          error={item.chartType === ""}
                          showMessage={item.chartType === ""}
                          message={t("rules.required")}
                        />
                      </div>
                      <div className="daily-setting-player-manual-scale-wrapper">
                        <p className="daily-setting-player-manual-scale-title">
                          {t("manualScale")}
                        </p>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={item.manualScaleActive || false}
                              onChange={(e) => {
                                setTempPlayerItems((prev) => {
                                  const updated = [...prev]
                                  updated[index].yAxis[itemIndex] = {
                                    ...updated[index].yAxis[itemIndex],
                                    manualScaleActive: e.target.checked,
                                  }

                                  return updated
                                })
                              }}
                              size="small"
                            />
                          }
                          label=""
                        />
                      </div>
                      <div className="daily-setting-player-max-value-wrapper">
                        <p className="daily-setting-player-max-value-title">{t("maxValue")}</p>
                        <TextField
                          value={
                            manualScaleValues[index]?.[itemIndex] !== undefined
                              ? manualScaleValues[index][itemIndex]
                              : item.manualScaleValue || ""
                          }
                          onChange={(e) => {
                            const inputValue = e.target.value

                            if (!/^\d*\.?\d*$/.test(inputValue) && inputValue !== "") {
                              return
                            }

                            setManualScaleValues((prev) => ({
                              ...prev,
                              [index]: {
                                ...prev[index],
                                [itemIndex]: inputValue,
                              },
                            }))

                            const numValue = inputValue === "" ? null : Number(inputValue)
                            debouncedUpdateManualScale(index, itemIndex, numValue)
                          }}
                          disabled={!item.manualScaleActive}
                        />
                      </div>
                      <ColorPicker
                        color={item.color}
                        setColor={(color) => {
                          setTempPlayerItems((prev) => {
                            const updated = [...prev]
                            updated[index].yAxis[itemIndex] = {
                              ...updated[index].yAxis[itemIndex],
                              color: color,
                            }

                            return updated
                          })
                        }}
                      />
                      <div className="daily-setting-player-yAxis-add-wrapper">
                        <IconButton
                          type="default"
                          icon="subtract"
                          size="md"
                          onClick={() => {
                            setTempPlayerItems((prev) => {
                              const updated = [...prev]
                              updated[index].yAxis = updated[index].yAxis.filter(
                                (_, i) => i !== itemIndex,
                              )

                              return updated
                            })
                          }}
                          disabled={itemIndex === 0}
                        />
                        <div className="add-button-placeholder">
                          {itemIndex === setting.yAxis.length - 1 && (
                            <IconButton
                              type="default"
                              icon="add"
                              size="md"
                              onClick={() => {
                                setTempPlayerItems((prev) => {
                                  const updated = [...prev]
                                  updated[index].yAxis.push({ ...DEFAULT_Y_AXIS })

                                  return updated
                                })
                              }}
                              disabled={itemIndex === 2}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              <Divider />
              <div key="sort" className="daily-setting-player-yAxis-row">
                <p className="daily-setting-player-yAxis-label">{t("sort")}</p>
                <SortSelector
                  value={setting.sortSetting || { key: "jersey", order: "asc" }}
                  onChange={(value) => {
                    setTempPlayerItems((prev) => {
                      const updated = [...prev]
                      updated[index] = { ...updated[index], sortSetting: value }

                      return updated
                    })
                  }}
                  options={getSortMenuOptions(setting)}
                />
              </div>
            </SettingSectionItem>
          )
        })}
      </div>

      <DeleteItemDialog
        deleteItemIndex={deleteItemIndex}
        setDeleteItemIndex={setDeleteItemIndex}
        onDeleteAction={(index) => setTempPlayerItems((prev) => prev.filter((_, i) => i !== index))}
      />
    </>
  )
}
